// Getting a card photo ready to keep. The original file is uploaded exactly as taken; alongside it the browser
// makes a display copy and a thumbnail. Drawing through a canvas turns the photo upright and drops all of its
// metadata (including where it was taken), so the copies the family sees carry none.
import { readRef, type Crop, type Tone } from '../../shared/crop'
import { ApiError } from './api'
import { decodeHeif, isHeif } from './heif'

// Long edges, in pixels. The display copy fills a big screen; the medium one is what a phone needs to fill its own
// (and what a tablet's grid needs to stay sharp); the thumbnail fills a grid tile on a phone.
export const DISPLAY = 2000
export const MEDIUM = 1200
export const THUMB = 480

// What the photo pickers offer. HEIC is named as well as image/*, so an iPhone hands over its photos as they are
// rather than converting them to JPEG first.
export const PHOTO_TYPES = 'image/*,image/heic,image/heif'

export type PreparedPhoto = {
  original: File
  display: Blob
  medium?: Blob
  thumb: Blob
  width: number
  height: number
  // The part that shows, when it was cropped before uploading; the copies above are already cropped.
  crop?: Crop | null
}

export type Copies = Pick<PreparedPhoto, 'display' | 'medium' | 'thumb'>

export class PhotoError extends Error {}

export async function preparePhoto(
  file: File,
  { medium = false, crop = null }: { medium?: boolean; crop?: Crop | null } = {},
): Promise<PreparedPhoto> {
  const bitmap = await openPhoto(file)
  try {
    const copies = await makeCopies(bitmap, crop, medium)
    return { original: file, ...copies, width: bitmap.width, height: bitmap.height, crop }
  } finally {
    bitmap.close()
  }
}

// The photo, upright, ready to draw: as the browser opens it, or with libheif for a HEIC it can't. A camera saves
// which way up it was held rather than turning the pixels; that is asked for by name, as older browsers ignored it.
export async function openPhoto(file: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    return openHeif(file)
  }
}

// The copies people see, made from the whole photo, or turned and cropped as the crop says. Each smaller copy is
// drawn from the one before it rather than from the full photo, which is much less work for a phone and no worse to
// look at; the three are then encoded together.
export async function makeCopies(bitmap: ImageBitmap, crop: Crop | null, medium: boolean): Promise<Copies> {
  const display = drawn(bitmap, crop ?? { x: 0, y: 0, width: bitmap.width, height: bitmap.height }, DISPLAY)
  const middle = medium ? drawn(display, whole(display), MEDIUM) : undefined
  const thumb = drawn(middle ?? display, whole(middle ?? display), THUMB)
  const [d, m, t] = await Promise.all([
    encoded(display, 0.82),
    middle ? encoded(middle, 0.82) : undefined,
    encoded(thumb, 0.8),
  ])
  return { display: d, medium: m, thumb: t }
}

const whole = (canvas: HTMLCanvasElement): Crop => ({ x: 0, y: 0, width: canvas.width, height: canvas.height })

// The part of a picture a crop keeps, turned and toned as it says, no longer than `longEdge` on either side.
export function drawn(
  source: CanvasImageSource & { width: number; height: number },
  part: Crop,
  longEdge: number,
): HTMLCanvasElement {
  const scale = Math.min(1, longEdge / Math.max(part.width, part.height))
  const width = Math.max(1, Math.round(part.width * scale))
  const height = Math.max(1, Math.round(part.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new PhotoError('This browser can’t prepare photos. Please try another browser.')
  ctx.imageSmoothingQuality = 'high'
  if (!part.turn) {
    ctx.drawImage(source, part.x, part.y, part.width, part.height, 0, 0, width, height)
    return toned(canvas, part.tone)
  }
  // The whole picture is drawn turned, moved so the part kept lands on the canvas; the rest falls off it.
  const { width: w, height: h } = source
  ctx.scale(width / part.width, height / part.height)
  ctx.translate(-part.x, -part.y)
  if (part.turn === 1) ctx.translate(h, 0)
  if (part.turn === 2) ctx.translate(w, h)
  if (part.turn === 3) ctx.translate(0, w)
  ctx.rotate(((part.turn ?? 0) * Math.PI) / 2)
  ctx.drawImage(source, 0, 0)
  ctx.resetTransform()
  return toned(canvas, part.tone)
}

// How each tone mixes red, green and blue into each of them: the same mixes as CSS grayscale() and sepia(), which the
// editor previews with. Done by hand because Safari's canvas has no filter.
const MIXES = {
  mono: [0.2126, 0.7152, 0.0722, 0.2126, 0.7152, 0.0722, 0.2126, 0.7152, 0.0722],
  sepia: [0.393, 0.769, 0.189, 0.349, 0.686, 0.168, 0.272, 0.534, 0.131],
} as const

export function toned(canvas: HTMLCanvasElement, tone: Tone | undefined) {
  if (!tone) return canvas
  const ctx = canvas.getContext('2d')!
  const image = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const px = image.data
  const [rr, rg, rb, gr, gg, gb, br, bg, bb] = MIXES[tone]
  for (let i = 0; i < px.length; i += 4) {
    const r = px[i]!
    const g = px[i + 1]!
    const b = px[i + 2]!
    // The array clamps to 0–255 as it's written.
    px[i] = rr * r + rg * g + rb * b
    px[i + 1] = gr * r + gg * g + gb * b
    px[i + 2] = br * r + bg * g + bb * b
  }
  ctx.putImageData(image, 0, 0)
  return canvas
}

// WebP where the browser can make it, JPEG where it can't. Safari quietly answers a WebP request with a PNG, which is
// slow to make and thrown away, so whether WebP works is learned once, on a single pixel.
async function encoded(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  if (await makesWebp()) {
    const webp = await toBlob(canvas, 'image/webp', quality)
    if (webp?.type === 'image/webp') return webp
  }
  const jpeg = await toBlob(canvas, 'image/jpeg', quality)
  if (!jpeg) throw new PhotoError('This browser can’t prepare photos. Please try another browser.')
  return jpeg
}

let webp: Promise<boolean> | null = null
function makesWebp() {
  return (webp ??= (() => {
    const canvas = document.createElement('canvas')
    canvas.width = canvas.height = 1
    return toBlob(canvas, 'image/webp', 0.8).then(
      (blob) => blob?.type === 'image/webp',
      () => false,
    )
  })())
}

// A HEIC this browser can't open itself, opened with libheif; anything else it can't open is refused.
async function openHeif(file: Blob) {
  const refused = new PhotoError(
    file instanceof File
      ? `“${file.name}” can’t be opened. Try a JPEG, PNG or HEIC photo, or take the photo here.`
      : 'This photo can’t be opened in this browser. Please try another browser.',
  )
  if (!(await isHeif(file).catch(() => false))) throw refused
  try {
    return await decodeHeif(file)
  } catch (e) {
    console.warn('a HEIC photo could not be decoded', e)
    throw refused
  }
}

const toBlob = (canvas: HTMLCanvasElement, type: string, quality: number) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality))

// Where a copy of a photo is, from its ref (see shared/crop.ts): once a photo is cropped its copies have a new
// address, so no browser keeps showing the old ones. The original never changes, so its address never does.
export function refUrl(path: string, ref: string, variant: string) {
  const { id, v } = readRef(ref)
  return `/api/${path}/${id}/${variant}${v && variant !== 'original' ? `?v=${v}` : ''}`
}

export const photoUrl = (ref: string, variant: 'original' | 'display' | 'thumb') => refUrl('photos', ref, variant)

// A crop (or null for the whole photo) and the copies made for it, for a photo already kept at `path` (its API
// address, "/album-photos/<id>"). Answers with the photo's new ref and size as it now shows.
export async function saveCrop(path: string, crop: Crop | null, copies: Copies) {
  const form = new FormData()
  if (crop) form.set('crop', JSON.stringify(crop))
  form.set('display', copies.display, 'display')
  if (copies.medium) form.set('medium', copies.medium, 'medium')
  form.set('thumb', copies.thumb, 'thumb')
  const res = await fetch(`/api${path}/crop`, { method: 'PUT', body: form, credentials: 'same-origin' })
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string }
    throw new ApiError(res.status, data.error ?? 'The crop didn’t save. Please try again.')
  }
  return (await res.json()) as { ref: string; width: number; height: number }
}

// Adds a crop sent with an upload; the copies uploaded alongside are already cropped.
export function setCrop(form: FormData, photo: Pick<PreparedPhoto, 'crop'>) {
  if (photo.crop) form.set('crop', JSON.stringify(photo.crop))
}
